import { buildCopyContextMenuItems, gridHeaderIconsSx, useClearCellRangeOnOutsideClick } from '@/components/ReportDashboard/Shared/GridActionsMenu'
import { COMPANY_COLORS, getColorWithOpacity } from '@/constants/colors.constant'
import { GfrItem } from '@/constants/ReportDashboard/gfr-items'
import { ReportItem } from '@/constants/ReportDashboard/report-items'
import {
  HoldingsMetric,
  ShowContributors,
  useHoldingsMetric,
  useTopBottomSecurities
} from '@/contexts/HoldingsTabContext'
import { usePortfolioBaseCCY } from '@/contexts/RADashboardDataContext/hooks'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { cardPrefsContextKey, CardProps, HoldingsSecurityDetails, TopBottomPrefs } from '@/interfaces/raDashboard.interface'
import { putUserPreference } from '@/services/userPreference.service'
import { getHeader } from '@/utils/factorAnalytics.util'
import { getGridThemeClass } from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ColDef, GridApi, GridReadyEvent, ModuleRegistry, ValueFormatterParams } from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'
import FilterIndicatorBox from '../Shared/FilterIndicatorBox'

ModuleRegistry.registerModules([ClientSideRowModelModule, CsvExportModule, ExcelExportModule, ClipboardModule, RangeSelectionModule, MenuModule])

// Raw, full-precision cell values for copy (bypass display formatters).
const clipboardCellValue = (p: any) => {
  const field = p.column?.getColDef?.()?.field
  return field && p.node?.data && p.node.data[field] !== undefined ? p.node.data[field] : p.value
}

const toLocaleNum = (value: any | null, decimalPlaces?: number) => {
  if (value == null) return ''
  return Number(value).toLocaleString(navigator.language, decimalPlaces || decimalPlaces === 0 ? { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces } : {})
}

const toFixedNum = (value: any | null, decimalPlaces?: number) => {
  if (value == null) return ''
  return Number(value).toFixed(decimalPlaces)
}

const METRIC_LABELS: Record<HoldingsMetric, string> = {
  securityRisk: 'Security Risk',
  securityRiskContrib: 'Contribution to Risk',
}

const SHOW_OPTIONS: { label: string; value: ShowContributors }[] = [
  { label: 'Top & Bottom', value: 'topBottom' },
  { label: 'Top Only', value: 'topOnly' },
  { label: 'Bottom Only', value: 'bottomOnly' },
]

const COUNT_OPTIONS = [5, 10, 15] as const

// ── ag-grid sub-table for one set (top or bottom) ──
interface SecurityTableProps {
  data: HoldingsSecurityDetails[]
  isTop: boolean
  metric: HoldingsMetric
  onApiReady?: (api: GridApi) => void
}

const SecurityTable = ({ data, isTop, metric, onApiReady }: SecurityTableProps) => {
  const theme = useTheme()
  const isDarkMode = useDarkModeCheck()
  const baseCCY = usePortfolioBaseCCY();
  const accentColor = isTop ? COMPANY_COLORS.ERROR : COMPANY_COLORS.SUCCESS
  const apiRef = useRef<GridApi | null>(null)
  const gridBoxRef = useRef<HTMLDivElement>(null)
  useClearCellRangeOnOutsideClick(gridBoxRef, () => apiRef.current)
  const gridContextMenuItems = useMemo(() => buildCopyContextMenuItems({ processCell: clipboardCellValue }), [])

  const numFmt = (p: ValueFormatterParams) => toFixedNum(p.value, 2)
  const mvFmt = (p: ValueFormatterParams) => toLocaleNum(p.value, 2)

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { headerName: getHeader(ReportItem.StandardID), field: 'identifier', flex: 1, minWidth: 110 },
      { headerName: getHeader(ReportItem.Description), field: 'issuer', flex: 2, minWidth: 140 },
      { headerName: getHeader(ReportItem.MarketValue).replace("(Base CCY)", "- "+baseCCY), field: 'marketValue', flex: 1, minWidth: 160, cellClass: 'ag-right-aligned-cell', valueFormatter: mvFmt },
      {
        headerName: getHeader(GfrItem.SecurityRisk),
        field: 'securityRisk',
        flex: 1,
        minWidth: 110,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: numFmt,
        cellStyle: () =>
          metric === 'securityRisk'
            ? { color: accentColor, fontWeight: 600 }
            : { color: theme.textPrimary, fontWeight: 400 },
      },
      {
        headerName: getHeader(GfrItem.SecurityRiskContrib),
        field: 'securityRiskContrib',
        flex: 1,
        minWidth: 140,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: numFmt,
        cellStyle: () =>
          metric === 'securityRiskContrib'
            ? { color: accentColor, fontWeight: 600 }
            : { color: theme.textPrimary, fontWeight: 400 },
      },
    ],
    [theme, metric, accentColor, baseCCY],
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({ sortable: true, filter: false, resizable: true }),
    [],
  )

  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      apiRef.current = e.api
      onApiReady?.(e.api)
    },
    [onApiReady],
  )


  return (
    <Box
      ref={gridBoxRef}
      className={getGridThemeClass(isDarkMode)}
      sx={[
        {
          width: '100%',
          // cwan theme drives colors/borders/fonts; keep only grid-specific gaps.
          '--ag-secondary-foreground-color': theme.textSecondary,
          '--ag-row-hover-color': getColorWithOpacity(theme.border, 0.4),
          '--ag-cell-horizontal-padding': '10px',
        } as React.CSSProperties,
        gridHeaderIconsSx,
      ]}
    >
      <AgGridReact
        rowData={data}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        domLayout='autoHeight'
        onGridReady={onGridReady}
        animateRows={false}
        columnHoverHighlight
        enableRangeSelection
        popupParent={document.body}
        getContextMenuItems={gridContextMenuItems}
        processCellForClipboard={clipboardCellValue}
      />
    </Box>
  )
}

const TopBottomTableCard: React.FC<CardProps> = (props) => {
  const theme = useTheme()
  const {
    metric,
    setMetric,
    showContributors,
    setShowContributors,
    contributorCount,
    setContributorCount,
    securitiesFiltered,
    highlightGroups,
  } = useHoldingsMetric()
  const topBotItems = useTopBottomSecurities()
  const [viewOptionsAnchor, setViewOptionsAnchor] =
    useState<null | HTMLElement>(null)

  const topApiRef = useRef<GridApi | null>(null)
  const bottomApiRef = useRef<GridApi | null>(null)
  const setTopApi = useCallback((api: GridApi) => { topApiRef.current = api }, [])
  const setBottomApi = useCallback((api: GridApi) => { bottomApiRef.current = api }, [])

  useEffect(() => {
    const saved = props.cardPrefs?.holdingsTopBottomTable
    if (saved) {
      setMetric(saved.metric)
      setShowContributors(saved.showContributors)
      setContributorCount(saved.contributorCount)
    }
  }, [props.cardPrefs])

  const savePrefs = (newPrefs: TopBottomPrefs) => {
    if (props.cardPrefs?.dashboardName) {
      const updated = { ...props.cardPrefs, holdingsTopBottomTable: newPrefs }
      props.onCardPrefsChange?.(updated)
      putUserPreference(cardPrefsContextKey(props.cardPrefs.dashboardName), updated)
    }
  }

  const { topItems, bottomItems } = topBotItems

  const cardTitle = useMemo(() => {
    const metricLabel = METRIC_LABELS[metric]
    if (showContributors === 'topOnly')
      return `Top ${contributorCount} by ${metricLabel}`
    if (showContributors === 'bottomOnly')
      return `Bottom ${contributorCount} by ${metricLabel}`
    return `Top ${contributorCount} / Bottom ${contributorCount} by ${metricLabel}`
  }, [metric, showContributors, contributorCount])

  const showTop = showContributors === 'topBottom' || showContributors === 'topOnly'
  const showBottom = showContributors === 'topBottom' || showContributors === 'bottomOnly'

  const downloadCsv = (fileName: string, content: string) => {
    const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleExport = (type: 'CSV' | 'EXCEL') => {
    setViewOptionsAnchor(null)
    // Raw, full-precision values — bypass the 2-decimal display formatter.
    const processCellCallback = (p: any) => {
      const field = p.column?.getColDef?.()?.field
      if (field && p.node?.data && p.node.data[field] !== undefined)
        return p.node.data[field]
      return p.value
    }
    // Only export a section that's shown AND has rows — skips a hidden section's
    // stale/destroyed grid ref and skips empty sheets/sections entirely.
    const tApi = showTop && topItems.length > 0 ? topApiRef.current : null
    const bApi = showBottom && bottomItems.length > 0 ? bottomApiRef.current : null
    if (type === 'EXCEL') {
      const sheets = [
        tApi?.getSheetDataForExcel({ sheetName: 'Top', processCellCallback }),
        bApi?.getSheetDataForExcel({ sheetName: 'Bottom', processCellCallback }),
      ].filter((s): s is string => !!s)
      const api = tApi ?? bApi
      if (api && sheets.length)
        api.exportMultipleSheetsAsExcel({ data: sheets, fileName: 'top_bottom_securities' })
    } else {
      const parts: string[] = []
      if (tApi)
        parts.push(`Top ${contributorCount} Contributors\n` + tApi.getDataAsCsv({ processCellCallback }))
      if (bApi)
        parts.push(`Bottom ${contributorCount} Contributors\n` + bApi.getDataAsCsv({ processCellCallback }))
      if (parts.length)
        downloadCsv('top_bottom_securities.csv', parts.join('\n\n'))
    }
  }

  const cardLayout = {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    padding: '12px',
    boxSizing: 'border-box' as const,
  }

  const menuPaperSx = {
    backgroundColor: theme.cardBackground,
    border: `1px solid ${theme.border}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    minWidth: 180,
  }

  const menuItemSx = {
    fontSize: '14px',
    color: theme.textPrimary,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    py: '6px',
    px: 2,
  }

  const sectionLabelSx = {
    px: 2,
    pt: 1,
    pb: 0.5,
    fontSize: '14px',
    fontWeight: 600,
    color: theme.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  }

  const toggleMetric = () => {
    const next: HoldingsMetric = metric === 'securityRisk' ? 'securityRiskContrib' : 'securityRisk'
    setMetric(next)
    savePrefs({ metric: next, showContributors, contributorCount })
  }

  const filteredBox = !securitiesFiltered ? null : <FilterIndicatorBox groups={highlightGroups} />

  return (
    <Stack
      sx={{
        ...cardStyle,
        ...cardLayout,
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      {/* Header */}
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='flex-start'
        sx={{ mb: '12px' }}
      >
        <Stack>
          <Typography
            variant='subtitle2'
            sx={{ fontWeight: 700, color: theme.textPrimary }}
          >
            {cardTitle} {filteredBox}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: theme.textSecondary }}>
            Top and bottom contributors to portfolio risk
          </Typography>
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          spacing={0.5}
          sx={{ flexShrink: 0, ml: 1 }}
        >
          <Box
            onClick={toggleMetric}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              fontSize: '14px',
              color: theme.textPrimary,
              cursor: 'pointer',
              backgroundColor: theme.cardBackground,
              whiteSpace: 'nowrap' as const,
              '&:hover': { backgroundColor: theme.border },
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 14 }} />
            {METRIC_LABELS[metric]}
          </Box>
          <IconButton
            size='small'
            onClick={(e) => setViewOptionsAnchor(e.currentTarget)}
            sx={{ color: theme.textSecondary, padding: '4px' }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* View Options + Export Menu */}
      <Menu
        anchorEl={viewOptionsAnchor}
        open={Boolean(viewOptionsAnchor)}
        onClose={() => setViewOptionsAnchor(null)}
        PaperProps={{ sx: menuPaperSx }}
      >
        <Typography sx={{ ...sectionLabelSx, color: theme.textPrimary, fontWeight: 700, textTransform: 'none' }}>
          View Options
        </Typography>
        <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
        <Typography sx={sectionLabelSx}>Show Contributors</Typography>
        {SHOW_OPTIONS.map(({ label, value }) => (
          <MenuItem
            key={value}
            onClick={() => {
              setShowContributors(value)
              setViewOptionsAnchor(null)
              savePrefs({ metric, showContributors: value, contributorCount })
            }}
            sx={menuItemSx}
          >
            <FiberManualRecordIcon
              sx={{
                fontSize: 10,
                color: showContributors === value ? theme.textPrimary : 'transparent',
                flexShrink: 0,
              }}
            />
            {label}
          </MenuItem>
        ))}
        <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
        <Typography sx={sectionLabelSx}>Number of Contributors</Typography>
        {COUNT_OPTIONS.map((count) => (
          <MenuItem
            key={count}
            onClick={() => {
              setContributorCount(count)
              setViewOptionsAnchor(null)
              savePrefs({ metric, showContributors, contributorCount: count })
            }}
            sx={menuItemSx}
          >
            <FiberManualRecordIcon
              sx={{
                fontSize: 10,
                color: contributorCount === count ? theme.textPrimary : 'transparent',
                flexShrink: 0,
              }}
            />
            {count} Contributors
          </MenuItem>
        ))}
        <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
        <Typography sx={sectionLabelSx}>Export</Typography>
        <MenuItem onClick={() => handleExport('EXCEL')} sx={menuItemSx}>
          Export as Excel
        </MenuItem>
        <MenuItem onClick={() => handleExport('CSV')} sx={menuItemSx}>
          Export as CSV
        </MenuItem>
      </Menu>

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {showTop && (
          <>
            <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mb: '6px' }}>
              <TrendingUpIcon sx={{ fontSize: 14, color: COMPANY_COLORS.ERROR }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.textPrimary }}>
                Top {contributorCount} Contributors
              </Typography>
            </Stack>
            <Box
              sx={{
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                mb: showContributors === 'topBottom' ? '16px' : 0,
                overflow: 'hidden',
              }}
            >
              <SecurityTable data={topItems} isTop={true} metric={metric} onApiReady={setTopApi} />
            </Box>
          </>
        )}
        {showBottom && (
          <>
            <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mb: '6px' }}>
              <TrendingDownIcon sx={{ fontSize: 14, color: COMPANY_COLORS.SUCCESS }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.textPrimary }}>
                Bottom {contributorCount} Contributors
              </Typography>
            </Stack>
            <Box
              sx={{
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <SecurityTable data={bottomItems} isTop={false} metric={metric} onApiReady={setBottomApi} />
            </Box>
          </>
        )}
      </Box>
    </Stack>
  )
}

export default TopBottomTableCard
